import type {
  ReportGenerationAdvancedVisualizationGraph,
  ReportGenerationAdvancedVisualizationGraphEdge,
  ReportGenerationAdvancedVisualizationGraphNode,
  ReportGenerationAdvancedVisualizationGraphqlOperation,
  ReportGenerationAdvancedVisualizationSnapshot,
} from './types';

const normalizeIdPart = (value: string | null | undefined): string =>
  String(value ?? 'unknown')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._:-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'unknown';

const normalizePivotIdPart = (value: string | null | undefined): string =>
  String(value ?? 'unknown')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9.:-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'unknown';

const displayValue = (value: string | null | undefined, fallback = 'Unknown'): string => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const displayPivotKey = (value: string | null | undefined): string => {
  const text = displayValue(value, 'Unknown pivot');
  if (/[A-Z]/.test(text) || text.includes(' ')) {
    return text;
  }
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\b[a-z]/g, (character) => character.toUpperCase())
    .replace(/\bId\b/g, 'ID');
};

const qualifiedLabel = (kind: string, value: string | null | undefined, fallback?: string): string =>
  `${kind}: ${displayValue(value, fallback)}`;

const joinDetail = (parts: Array<string | null | undefined>): string | undefined => {
  const detail = parts
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' / ');
  return detail || undefined;
};

const addNode = (
  nodes: Map<string, ReportGenerationAdvancedVisualizationGraphNode>,
  node: ReportGenerationAdvancedVisualizationGraphNode,
) => {
  if (!nodes.has(node.id)) {
    nodes.set(node.id, node);
  }
};

const addEdge = (
  edges: Map<string, ReportGenerationAdvancedVisualizationGraphEdge>,
  edge: ReportGenerationAdvancedVisualizationGraphEdge,
) => {
  if (edge.source === edge.target || edges.has(edge.id)) {
    return;
  }
  edges.set(edge.id, edge);
};

const operationNodeId = (operation: ReportGenerationAdvancedVisualizationGraphqlOperation): string =>
  `operation:${normalizeIdPart(operation.id)}:${normalizeIdPart(operation.appSyncField)}:${normalizeIdPart(operation.resultPath)}`;

const functionNodeId = (operation: ReportGenerationAdvancedVisualizationGraphqlOperation): string =>
  `function:${normalizeIdPart(operation.appSyncRouter)}:${normalizeIdPart(operation.appSyncField)}`;

const schemaNodeId = (schema: string): string => `schema:${normalizeIdPart(schema)}`;

const pivotNodeId = (joinKey: string): string => `pivot:${normalizePivotIdPart(joinKey)}`;

const collectOperations = (
  snapshot: ReportGenerationAdvancedVisualizationSnapshot,
): ReportGenerationAdvancedVisualizationGraphqlOperation[] => {
  const operations = new Map<string, ReportGenerationAdvancedVisualizationGraphqlOperation>();
  for (const operation of snapshot.graphqlOperations) {
    const id = operationNodeId(operation);
    if (!operations.has(id)) {
      operations.set(id, operation);
    }
  }
  return [...operations.values()].sort((left, right) => operationNodeId(left).localeCompare(operationNodeId(right)));
};

export const projectReportGenerationAdvancedVisualizationGraph = (
  snapshot: ReportGenerationAdvancedVisualizationSnapshot,
): ReportGenerationAdvancedVisualizationGraph => {
  const nodes = new Map<string, ReportGenerationAdvancedVisualizationGraphNode>();
  const edges = new Map<string, ReportGenerationAdvancedVisualizationGraphEdge>();
  const routeNodeId = `route:${normalizeIdPart(snapshot.pathname)}`;
  const reportNodeId = `report:${normalizeIdPart(snapshot.title)}`;
  const actorNodeId = `actor:${normalizeIdPart(snapshot.actor.source)}:${normalizeIdPart(snapshot.actor.persona)}`;
  const entityLabels = new Map<string, string>();

  for (const module of snapshot.modules) {
    for (const entity of module.entities) {
      entityLabels.set(entity.id, entity.label);
    }
  }

  addNode(nodes, {
    id: routeNodeId,
    kind: 'route',
    label: qualifiedLabel('Route', snapshot.pathname),
    detail: `${snapshot.module} / ${snapshot.routeFamily}`,
    status: 'neutral',
  });
  addNode(nodes, {
    id: reportNodeId,
    kind: 'report',
    label: qualifiedLabel('Report', snapshot.title),
    detail: `${snapshot.selectedEntityCount} entities, ${snapshot.selectedFieldCount} fields`,
    status: snapshot.warnings.length > 0 ? 'limited' : 'allowed',
  });
  addEdge(edges, {
    id: `${routeNodeId}->${reportNodeId}:renders`,
    source: routeNodeId,
    target: reportNodeId,
    label: 'renders',
  });

  addNode(nodes, {
    id: actorNodeId,
    kind: 'actor',
    label: qualifiedLabel('Actor', snapshot.actor.persona),
    detail: `${snapshot.actor.groups.length} groups, ${snapshot.actor.permissions.length} permissions`,
    status: 'allowed',
  });
  addEdge(edges, {
    id: `${actorNodeId}->${reportNodeId}:scopes`,
    source: actorNodeId,
    target: reportNodeId,
    label: 'scopes',
  });

  for (const permission of snapshot.actor.permissions) {
    const permissionNodeId = `permission:${normalizeIdPart(permission)}`;
    addNode(nodes, {
      id: permissionNodeId,
      kind: 'permission',
      label: qualifiedLabel('Perm', permission),
      status: 'allowed',
    });
    addEdge(edges, {
      id: `${actorNodeId}->${permissionNodeId}:has`,
      source: actorNodeId,
      target: permissionNodeId,
      label: 'has',
    });
  }

  const datasourceFamilies = [...new Set([
    ...snapshot.datasourceFamilies,
    ...(snapshot.datasourcePlan?.requiredDatasourceFamilies ?? []),
    ...(snapshot.datasourcePlan?.selectedDatasourceFamilies ?? []),
  ])].sort();
  for (const family of datasourceFamilies) {
    const familyNodeId = `datasource:${normalizeIdPart(family)}`;
    const selected = snapshot.datasourcePlan?.selectedDatasourceFamilies.includes(family) ?? true;
    addNode(nodes, {
      id: familyNodeId,
      kind: 'datasource',
      label: qualifiedLabel('Source', family),
      detail: selected ? 'selected datasource family' : 'required but not selected',
      status: selected ? 'allowed' : 'blocked',
    });
    addEdge(edges, {
      id: `${reportNodeId}->${familyNodeId}:uses`,
      source: reportNodeId,
      target: familyNodeId,
      label: 'uses',
    });
  }

  const executionPlan = snapshot.datasourcePlan?.executionPlan ?? [];
  for (const step of executionPlan) {
    const familyNodeId = `datasource:${normalizeIdPart(step.datasourceFamily)}`;
    const adapterNodeId = `adapter:${normalizeIdPart(step.executionAdapter)}`;
    addNode(nodes, {
      id: adapterNodeId,
      kind: 'adapter',
      label: qualifiedLabel('Adapter', step.executionAdapter),
      detail: `datasource ${step.datasourceFamily}`,
      status: 'allowed',
    });
    addEdge(edges, {
      id: `${familyNodeId}->${adapterNodeId}:executes-through`,
      source: familyNodeId,
      target: adapterNodeId,
      label: 'executes through',
    });
  }

  for (const operation of collectOperations(snapshot)) {
    const nodeId = operationNodeId(operation);
    const resolverNodeId = functionNodeId(operation);
    addNode(nodes, {
      id: nodeId,
      kind: 'operation',
      label: qualifiedLabel(operation.kind === 'mutation' ? 'Mutation' : 'Query', operation.id),
      detail: joinDetail([
        `${operation.appSyncRouter}.${operation.appSyncField}`,
        operation.resultPath,
        operation.domainType,
      ]),
      status: 'allowed',
    });
    addNode(nodes, {
      id: resolverNodeId,
      kind: 'function',
      label: qualifiedLabel('Function', operation.appSyncField),
      detail: joinDetail([
        operation.appSyncRouter,
        operation.datasourceFamily,
        operation.domainPackage,
      ]),
      status: 'allowed',
    });
    addEdge(edges, {
      id: `${reportNodeId}->${nodeId}:previews-with`,
      source: reportNodeId,
      target: nodeId,
      label: 'previews with',
    });
    addEdge(edges, {
      id: `${nodeId}->${resolverNodeId}:resolved-by`,
      source: nodeId,
      target: resolverNodeId,
      label: 'resolved by',
    });
    if (operation.datasourceFamily) {
      addEdge(edges, {
        id: `${resolverNodeId}->datasource:${normalizeIdPart(operation.datasourceFamily)}:reads`,
        source: resolverNodeId,
        target: `datasource:${normalizeIdPart(operation.datasourceFamily)}`,
        label: 'reads',
      });
    }
    if (operation.domainSchema) {
      const domainSchemaNodeId = schemaNodeId(operation.domainSchema);
      addNode(nodes, {
        id: domainSchemaNodeId,
        kind: 'schema',
        label: qualifiedLabel('Schema', operation.domainSchema),
        detail: joinDetail([operation.domainType, operation.domainPackage]),
        status: 'allowed',
      });
      addEdge(edges, {
        id: `${nodeId}->${domainSchemaNodeId}:returns`,
        source: nodeId,
        target: domainSchemaNodeId,
        label: 'returns',
      });
    }
  }

  for (const module of [...snapshot.modules].sort((left, right) => left.moduleId.localeCompare(right.moduleId))) {
    const moduleNodeId = `module:${normalizeIdPart(module.moduleId)}`;
    addNode(nodes, {
      id: moduleNodeId,
      kind: 'module',
      label: qualifiedLabel('Module', module.moduleLabel),
      detail: module.routeRoot,
      status: 'neutral',
    });
    addEdge(edges, {
      id: `${reportNodeId}->${moduleNodeId}:contains`,
      source: reportNodeId,
      target: moduleNodeId,
      label: 'contains',
    });

    for (const entity of [...module.entities].sort((left, right) => left.id.localeCompare(right.id))) {
      const entityNodeId = `entity:${normalizeIdPart(entity.id)}`;
      addNode(nodes, {
        id: entityNodeId,
        kind: 'entity',
        label: qualifiedLabel('Entity', entity.label),
        detail: joinDetail([entity.authorityScope, entity.authorityStatus, entity.permission]),
        status: entity.permissionResolution?.allowed === false ? 'blocked' : 'allowed',
      });
      addEdge(edges, {
        id: `${moduleNodeId}->${entityNodeId}:owns`,
        source: moduleNodeId,
        target: entityNodeId,
        label: 'owns',
      });

      if (entity.permission) {
        const permissionNodeId = `permission:${normalizeIdPart(entity.permission)}`;
        addNode(nodes, {
          id: permissionNodeId,
          kind: 'permission',
          label: qualifiedLabel('Perm', entity.permission),
          detail: entity.permissionResolution?.reason ?? undefined,
          status: entity.permissionResolution?.allowed === false ? 'blocked' : 'allowed',
        });
        addEdge(edges, {
          id: `${permissionNodeId}->${entityNodeId}:authorizes`,
          source: permissionNodeId,
          target: entityNodeId,
          label: 'authorizes',
        });
      }

      for (const field of [...entity.fields].sort((left, right) => left.id.localeCompare(right.id))) {
        const fieldNodeId = `field:${normalizeIdPart(field.id)}`;
        addNode(nodes, {
          id: fieldNodeId,
          kind: 'field',
          label: qualifiedLabel('Field', field.label),
          detail: joinDetail([
            field.authority?.domainSchema,
            field.authority?.domainType ?? field.type,
            field.authority?.resultPath,
          ]),
          status: field.authority ? 'allowed' : 'limited',
        });
        addEdge(edges, {
          id: `${entityNodeId}->${fieldNodeId}:selects`,
          source: entityNodeId,
          target: fieldNodeId,
          label: 'selects',
        });
        if (field.authority?.operationId) {
          const matchingOperation = snapshot.graphqlOperations.find((operation) => (
            operation.id === field.authority?.operationId
            && operation.appSyncField === field.authority?.appSyncField
            && operation.resultPath === field.authority?.resultPath
          ));
          if (matchingOperation) {
            addEdge(edges, {
              id: `${operationNodeId(matchingOperation)}->${fieldNodeId}:projects`,
              source: operationNodeId(matchingOperation),
              target: fieldNodeId,
              label: 'projects',
            });
          }
        }
        if (field.authority?.domainSchema) {
          const domainSchemaNodeId = schemaNodeId(field.authority.domainSchema);
          addNode(nodes, {
            id: domainSchemaNodeId,
            kind: 'schema',
            label: qualifiedLabel('Schema', field.authority.domainSchema),
            detail: joinDetail([field.authority.domainType, field.authority.domainPackage]),
            status: 'allowed',
          });
          addEdge(edges, {
            id: `${domainSchemaNodeId}->${fieldNodeId}:types-field`,
            source: domainSchemaNodeId,
            target: fieldNodeId,
            label: 'types field',
          });
          addEdge(edges, {
            id: `${domainSchemaNodeId}->${entityNodeId}:types-entity`,
            source: domainSchemaNodeId,
            target: entityNodeId,
            label: 'types entity',
          });
        }
        if (field.authority?.joinKey) {
          const fieldPivotNodeId = pivotNodeId(field.authority.joinKey);
          addNode(nodes, {
            id: fieldPivotNodeId,
            kind: 'pivot',
            label: qualifiedLabel('Pivot', displayPivotKey(field.authority.joinKey)),
            detail: joinDetail(['field join key', field.authority.joinKey, entity.label]),
            status: 'allowed',
          });
          addEdge(edges, {
            id: `${fieldNodeId}->${fieldPivotNodeId}:pivots-on`,
            source: fieldNodeId,
            target: fieldPivotNodeId,
            label: 'pivots on',
          });
        }
      }
    }
  }

  for (const join of [...snapshot.joins].sort((left, right) => left.id.localeCompare(right.id))) {
    const joinNodeId = `join:${normalizeIdPart(join.id)}`;
    addNode(nodes, {
      id: joinNodeId,
      kind: 'join',
      label: qualifiedLabel('Link', join.label),
      detail: joinDetail([
        join.joinKey ? `pivot ${displayPivotKey(join.joinKey)}` : null,
        join.leftEntityId ? `left ${entityLabels.get(join.leftEntityId) ?? join.leftEntityId}` : null,
        join.rightEntityId ? `right ${entityLabels.get(join.rightEntityId) ?? join.rightEntityId}` : null,
      ]),
      status: 'allowed',
    });
    addEdge(edges, {
      id: `${reportNodeId}->${joinNodeId}:joins`,
      source: reportNodeId,
      target: joinNodeId,
      label: 'joins',
    });
    for (const endpoint of [join.leftEntityId, join.rightEntityId]) {
      if (!endpoint) {
        continue;
      }
      addEdge(edges, {
        id: `${joinNodeId}->entity:${normalizeIdPart(endpoint)}:endpoint`,
        source: joinNodeId,
        target: `entity:${normalizeIdPart(endpoint)}`,
        label: 'endpoint',
      });
    }
    if (join.joinKey) {
      const joinPivotNodeId = pivotNodeId(join.joinKey);
      addNode(nodes, {
        id: joinPivotNodeId,
        kind: 'pivot',
        label: qualifiedLabel('Pivot', displayPivotKey(join.joinKey)),
        detail: joinDetail(['entity link join key', join.joinKey]),
        status: 'allowed',
      });
      addEdge(edges, {
        id: `${joinNodeId}->${joinPivotNodeId}:uses-pivot`,
        source: joinNodeId,
        target: joinPivotNodeId,
        label: 'uses pivot',
      });
    }
  }

  for (const blockedEntityId of [...snapshot.blockedEntityIds].sort()) {
    const statusNodeId = `status:blocked-entity:${normalizeIdPart(blockedEntityId)}`;
    addNode(nodes, {
      id: statusNodeId,
      kind: 'status',
      label: `Blocked entity: ${blockedEntityId}`,
      status: 'blocked',
    });
    addEdge(edges, {
      id: `${statusNodeId}->${reportNodeId}:blocks`,
      source: statusNodeId,
      target: reportNodeId,
      label: 'blocks',
    });
  }

  snapshot.warnings.forEach((warning, index) => {
    const statusNodeId = `status:warning:${index}`;
    addNode(nodes, {
      id: statusNodeId,
      kind: 'status',
      label: warning,
      status: 'limited',
    });
    addEdge(edges, {
      id: `${statusNodeId}->${reportNodeId}:limits`,
      source: statusNodeId,
      target: reportNodeId,
      label: 'limits',
    });
  });

  return {
    nodes: [...nodes.values()].sort((left, right) => left.id.localeCompare(right.id)),
    edges: [...edges.values()].sort((left, right) => left.id.localeCompare(right.id)),
  };
};
